/**
 * Client identity, preferences, appearance, and realtime presence. Persisted identity is a
 * hydration hint; AuthProvider remains responsible for verifying the API session. Appearance is
 * restored from the historical storage key during module initialization.
 *
 * @module apps/web/src/stores/userStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Persisted browser identity projection and display/planning preferences. The server session remains authoritative; clearing this store resets identity and onboarding fields, not every application store. */
export interface UserState {
  /** Cached current-account identifier, or null when the client identity is cleared. */
  userId: string | null;
  /** Cached account email; the server session remains authoritative. */
  email: string | null;
  /** Cached display name, or null when not supplied. */
  displayName: string | null;
  /** Versioned profile-picture path under the API root, or null when the account has no picture. */
  avatarUrl: string | null;
  /** Timezone used for local planning, falling back to the browser timezone. */
  timezone: string;
  /** Cached onboarding completion flag restored from account data. */
  onboardingCompleted: boolean;
  /** Normalized client appearance and planning preferences. */
  settings: {
    /** Light, dark, or system appearance selection. */
    theme: 'light' | 'dark' | 'system';
    /** Planner view selected by default. */
    defaultView: 'day' | 'week' | 'month' | 'kanban' | 'table';
    /** Six-digit RGB accent color normalized before applying CSS variables. */
    accentColor: string;
    /** Spacing preset applied to calendar presentation. */
    calendarDensity: 'compact' | 'balanced' | 'spacious';
    /** Local working-time boundaries merged separately during normalization. */
    workHours: {
      /** Workday start in local HH:MM clock notation. */ start: string;
      /** Workday end in local HH:MM clock notation. */ end: string;
    };
    /** Working weekday indices; empty input falls back to default weekdays. */
    workDays: number[];
    /** First weekday in calendar presentation: Sunday 0 or Monday 1. */
    weekStartsOn: 0 | 1; // 0 = Sunday, 1 = Monday
    /** Whether proposed unconfirmed placements should be displayed. */
    showGhostBlocks: boolean;
    /** Client preference enabling notifications. */
    enableNotifications: boolean;
    /** Client preference enabling audible feedback. */
    soundEnabled: boolean;
  };

  // WebSocket
  /** Transient realtime socket connection state; not persisted. */
  isConnected: boolean;
  /** Transient peer presence records reported by realtime messages. */
  connectedUsers: Array<{
    /** Account identifier reported for a connected peer. */ userId: string;
    /** Device identifier distinguishing a peer's browser connection. */ deviceId: string;
    /** Optional planner view reported by the connected peer. */ view?: string;
  }>;

  // Actions
  /** Merge account identity and preferences into the store and apply appearance. */
  setUser: (user: {
    /** Incoming server account identifier projected into userId. */
    id: string;
    /** Cached account email; the server session remains authoritative. */
    email: string;
    /** Cached display name, or null when not supplied. */
    displayName?: string;
    /** Profile-picture path; omit to keep the cached value, null to clear it. */
    avatarUrl?: string | null;
    /** Timezone used for local planning, falling back to the browser timezone. */
    timezone?: string;
    /** Cached onboarding completion flag restored from account data. */
    onboardingCompleted?: boolean;
    /** Partial incoming preferences merged onto the current normalized settings. */
    settings?: Partial<UserState['settings']>;
  }) => void;
  /** Clear identity and onboarding fields while retaining current preferences. */
  clearUser: () => void;
  /** Apply a saved profile edit so the header and other readers update at once; omitted fields are kept. */
  setProfile: (profile: {
    /** Saved display name. */
    displayName?: string | null;
    /** Saved picture path, or null after removal. */
    avatarUrl?: string | null;
  }) => void;
  /** Merge preference updates, normalize them, and apply appearance to the document. */
  updateSettings: (settings: Partial<UserState['settings']>) => void;
  /** Update transient realtime connectivity. */
  setConnected: (connected: boolean) => void;
  /** Replace the transient connected-peer list. */
  setConnectedUsers: (users: UserState['connectedUsers']) => void;
}

/** Fallback planner appearance and work preferences used when server or stored settings omit fields; these values do not represent measured user behavior. */
const defaultSettings: UserState['settings'] = {
  theme: 'system',
  defaultView: 'week',
  accentColor: '#13D8BD',
  calendarDensity: 'balanced',
  workHours: { start: '09:00', end: '17:00' },
  workDays: [1, 2, 3, 4, 5],
  weekStartsOn: 0,
  showGhostBlocks: true,
  enableNotifications: true,
  soundEnabled: true,
};

/**
 * Persist identity and normalized planner preferences under flexiplanner-user, while keeping
 * connection/presence state transient. Setting the user or preferences applies theme, accent CSS
 * variables, and calendar density; clearing the user clears identity and onboarding state.
 */
export const useUserStore = create<UserState>()(
  persist(
    set => ({
      userId: null,
      email: null,
      displayName: null,
      avatarUrl: null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      onboardingCompleted: false,
      settings: defaultSettings,
      isConnected: false,
      connectedUsers: [],

      setUser: user => {
        set(state => {
          const settings = keepUnchangedParts(
            state.settings,
            normalizeSettings(
              user.settings ? { ...state.settings, ...user.settings } : state.settings
            )
          );
          applyAppearance(settings);
          return {
            userId: user.id,
            email: user.email,
            displayName: user.displayName || null,
            avatarUrl: user.avatarUrl === undefined ? state.avatarUrl : user.avatarUrl,
            timezone: user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            onboardingCompleted: user.onboardingCompleted ?? state.onboardingCompleted,
            settings,
          };
        });
      },

      clearUser: () => {
        set({
          userId: null,
          email: null,
          displayName: null,
          avatarUrl: null,
          onboardingCompleted: false,
        });
      },

      setProfile: profile => {
        set(state => ({
          displayName: profile.displayName === undefined ? state.displayName : profile.displayName,
          avatarUrl: profile.avatarUrl === undefined ? state.avatarUrl : profile.avatarUrl,
        }));
      },

      updateSettings: newSettings => {
        set(state => {
          const settings = keepUnchangedParts(
            state.settings,
            normalizeSettings({ ...state.settings, ...newSettings })
          );
          applyAppearance(settings);
          return { settings };
        });
      },

      setConnected: connected => set({ isConnected: connected }),
      setConnectedUsers: users => set({ connectedUsers: users }),
    }),
    {
      name: 'flexiplanner-user',
      partialize: state => ({
        userId: state.userId,
        email: state.email,
        displayName: state.displayName,
        avatarUrl: state.avatarUrl,
        timezone: state.timezone,
        onboardingCompleted: state.onboardingCompleted,
        settings: state.settings,
      }),
    }
  )
);

/** Merge server or persisted preferences onto defaults, merging work-hour fields separately. Empty work-day lists use defaults; invalid accent colors and unknown density values are replaced. */
function normalizeSettings(settings: Partial<UserState['settings']>): UserState['settings'] {
  return {
    ...defaultSettings,
    ...settings,
    workHours: {
      ...defaultSettings.workHours,
      ...(settings.workHours || {}),
    },
    workDays: settings.workDays?.length ? settings.workDays : defaultSettings.workDays,
    accentColor: normalizeHex(settings.accentColor) || defaultSettings.accentColor,
    calendarDensity: ['compact', 'balanced', 'spacious'].includes(String(settings.calendarDensity))
      ? (settings.calendarDensity as UserState['settings']['calendarDensity'])
      : defaultSettings.calendarDensity,
  };
}

/**
 * Reuse `previous.workHours` and `previous.workDays` when `next` holds equal values. Calendars
 * select these nested objects; a theme toggle that rebuilt them would re-render every calendar
 * grid although no working-time value changed.
 */
function keepUnchangedParts(
  previous: UserState['settings'],
  next: UserState['settings']
): UserState['settings'] {
  const sameHours =
    previous.workHours.start === next.workHours.start &&
    previous.workHours.end === next.workHours.end;
  const sameDays =
    previous.workDays.length === next.workDays.length &&
    previous.workDays.every((day, index) => day === next.workDays[index]);
  return {
    ...next,
    workHours: sameHours ? previous.workHours : next.workHours,
    workDays: sameDays ? previous.workDays : next.workDays,
  };
}

/** Accept exactly six hexadecimal RGB digits with an optional leading hash and return uppercase #RRGGBB. Other lengths and CSS color forms return null. */
function normalizeHex(value?: string): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed.toUpperCase();
  if (/^[0-9a-f]{6}$/i.test(trimmed)) return `#${trimmed.toUpperCase()}`;
  return null;
}

/** Convert a validated six-digit RGB color into the space-separated HSL channels used by CSS custom properties. Invalid colors return null. */
function hexToHsl(value: string): string | null {
  const hex = normalizeHex(value);
  if (!hex) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const delta = max - min;
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    switch (max) {
      case r:
        h = (g - b) / delta + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      default:
        h = (r - g) / delta + 4;
        break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Pending browser frame for restoring ordinary transitions after a theme swap. */
let themeTransitionFrame: number | undefined;

/**
 * UI binding: suppress inherited color transitions through the next painted theme frame.
 * A second frame restores hover feedback without forcing synchronous layout. A newer swap
 * cancels the pending restoration, so rapid toggles keep suppression until their own paint.
 * Background tabs restore on their next rendered frames when animation frames resume.
 */
function suppressThemeTransitions(root: HTMLElement) {
  if (themeTransitionFrame !== undefined) window.cancelAnimationFrame(themeTransitionFrame);
  root.dataset.themeChanging = '';
  themeTransitionFrame = window.requestAnimationFrame(() => {
    themeTransitionFrame = window.requestAnimationFrame(() => {
      delete root.dataset.themeChanging;
      themeTransitionFrame = undefined;
    });
  });
}

/** Apply theme classes, accent variables, and calendar density to the document root. System theme is sampled at this call; this function does not install a media-query listener. */
function applyAppearance(settings: UserState['settings']) {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  const theme = settings.theme || defaultSettings.theme;
  const dark =
    theme === 'dark'
      ? true
      : theme === 'light'
        ? false
        : window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (root.classList.contains('dark') !== dark) {
    suppressThemeTransitions(root);
    root.classList.toggle('dark', dark);
  }

  const accent = normalizeHex(settings.accentColor) || defaultSettings.accentColor;
  const hsl = hexToHsl(accent);
  // A root custom property write can invalidate style across the whole document, so unchanged
  // values are left alone (a theme toggle does not change the accent).
  if (hsl) {
    setRootProperty(root, '--primary', hsl);
    setRootProperty(root, '--ring', hsl);
    setRootProperty(root, '--planner-accent', accent);
  }
  const density = settings.calendarDensity || defaultSettings.calendarDensity;
  if (root.dataset.density !== density) root.dataset.density = density;
}

/** Set one custom property on the document root only when its value differs. */
function setRootProperty(root: HTMLElement, name: string, value: string) {
  if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
}

// Initialize appearance on load
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('flexiplanner-user');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      applyAppearance(normalizeSettings(state?.settings || {}));
    } catch {
      // Unreadable stored settings keep the page's current appearance.
    }
  } else {
    applyAppearance(defaultSettings);
  }
}
